import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Minus, Plus } from "lucide-react";
import { CollectionPlaceholder } from "@/components/CollectionPlaceholder";
import { ProductGallery } from "@/components/ProductGallery";
import { ProductReviews, ReviewSummary } from "@/components/ProductReviews";
import { statsFromRatings, useProductReviews } from "@/lib/reviews";
import { supabase } from "@/integrations/supabase/client";

import { productDetails, type ProductDetail } from "@/lib/catalog";
import {
  fetchShopifyProducts,
  findVariantId,
  registerProducts,
  type ShopProduct,
} from "@/lib/shopify-catalog";
import { formatDH, useShop } from "@/lib/shop-store";
import { cn } from "@/lib/utils";

const SITE = "https://sunsetconcept.store";

/** URL absolue pour les métadonnées (images locales ou CDN Shopify). */
const abs = (src: string) => (src.startsWith("http") ? src : `${SITE}${src}`);

/** Description SEO : phrase complète, coupée proprement sous 160 caractères. */
function seoDescription(text: string) {
  if (text.length <= 155) return text;
  const cut = text.slice(0, 155);
  const stop = Math.max(cut.lastIndexOf("."), cut.lastIndexOf(","), cut.lastIndexOf(" "));
  return `${cut.slice(0, stop > 80 ? stop : 155).trim()}…`;
}

export const Route = createFileRoute("/produit/$slug")({
  /**
   * Les fiches produit sont alimentées par Shopify (titre, prix, variantes, stock).
   * En cas d'indisponibilité de l'API, on retombe sur la fiche locale existante.
   */
  loader: async ({ params }) => {
    // Agrégat d'avis publiés (utilisé pour aggregateRating dans le JSON-LD).
    const reviewsPromise = Promise.resolve(
      supabase.from("product_reviews").select("rating").eq("product_slug", params.slug),
    )
      .then(({ data }: { data: { rating: number }[] | null }) => {
        const ratings = (data ?? []).map((r: { rating: number }) => Number(r.rating)).filter((n: number) => n > 0);
        if (!ratings.length) return null;
        return {
          count: ratings.length,
          average: Math.round((ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length) * 10) / 10,
        };
      })
      .catch(() => null);

    try {
      const [products, reviews] = await Promise.all([fetchShopifyProducts(), reviewsPromise]);
      const product =
        products.find((p: ShopProduct) => p.slug === params.slug || p.handle === params.slug) ?? null;
      return { product, reviews };
    } catch (err) {
      console.error("Shopify produit:", err);
      return { product: null as ShopProduct | null, reviews: await reviewsPromise };
    }
  },
  head: ({ params, loaderData }) => {
    const product: ProductDetail | undefined =
      (loaderData as { product: ShopProduct | null } | undefined)?.product ??
      productDetails[params.slug];
    const reviewStats =
      (loaderData as { reviews?: { count: number; average: number } | null } | undefined)
        ?.reviews ?? null;
    // Métadonnées SEO rédigées à la main dans le catalogue si disponibles.
    const local = productDetails[params.slug];
    const title =
      local?.seoTitle ??
      (product ? `${product.name} — Sunset Concept Store` : "Pièce Sunset Concept Store");
    const description = seoDescription(
      local?.seoDescription ??
        product?.shortDescription ??
        "Détail d'une pièce de la sélection Sunset Concept Store, women's wear et resort.",
    );
    const url = `${SITE}/produit/${params.slug}`;
    const shareImage = product?.ogImage ?? product?.image ?? null;
    const image = shareImage ? abs(shareImage) : null;


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:site_name", content: "Sunset Concept Store" },
        { property: "og:locale", content: "fr_FR" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { property: "og:url", content: url },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:secure_url", content: image },
              { property: "og:image:type", content: image.endsWith(".jpg") ? "image/jpeg" : "image/webp" },
              ...(product?.ogImage
                ? [
                    { property: "og:image:width", content: "1200" },
                    { property: "og:image:height", content: "630" },
                  ]
                : []),
              { property: "og:image:alt", content: product!.name },
              { name: "twitter:image", content: image },
              { name: "twitter:image:alt", content: product!.name },
            ]
          : []),
        ...(product
          ? [
              { property: "product:brand", content: "Sunset Concept Store" },
              { property: "product:availability", content: "in stock" },
              { property: "product:condition", content: "new" },
              { property: "product:price:amount", content: String(product.price) },
              { property: "product:price:currency", content: "MAD" },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: product
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                "@id": `${url}#product`,
                name: product.name,
                description: product.shortDescription,
                image: [
                  ...(product.colors?.flatMap((c) => c.images ?? []) ?? []),
                  ...(product.images ?? []),
                  product.image ?? "",
                ]
                  .filter(Boolean)
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .map((i) => abs(i)),
                category: `${product.category} > ${product.subcategory}`,
                ...(product.colors?.length
                  ? { color: product.colors.map((c) => c.label).join(", ") }
                  : product.colorLabel
                    ? { color: product.colorLabel }
                    : {}),
                ...(product.sizes?.length
                  ? { size: product.sizes.map((s) => s.size) }
                  : product.sizeLabel
                    ? { size: product.sizeLabel }
                    : {}),
                sku: product.sizes?.[0]?.sku ?? params.slug.toUpperCase(),
                mpn: product.sizes?.[0]?.sku ?? params.slug.toUpperCase(),
                brand: { "@type": "Brand", name: "Sunset Concept Store" },
                ...(reviewStats
                  ? {
                      aggregateRating: {
                        "@type": "AggregateRating",
                        ratingValue: reviewStats.average.toFixed(1),
                        reviewCount: reviewStats.count,
                        bestRating: "5",
                        worstRating: "1",
                      },
                    }
                  : {}),
                offers: {
                  "@type": "Offer",
                  url,
                  price: product.price.toFixed(2),
                  priceCurrency: "MAD",
                  priceValidUntil: `${new Date().getUTCFullYear() + 1}-12-31`,
                  itemCondition: "https://schema.org/NewCondition",
                  availability: "https://schema.org/InStock",
                  seller: { "@type": "Organization", name: "Sunset Concept Store" },
                  areaServed: "MA",
                  shippingDetails: {
                    "@type": "OfferShippingDetails",
                    shippingRate: {
                      "@type": "MonetaryAmount",
                      value: "0",
                      currency: "MAD",
                    },
                    shippingDestination: {
                      "@type": "DefinedRegion",
                      addressCountry: "MA",
                    },
                    deliveryTime: {
                      "@type": "ShippingDeliveryTime",
                      handlingTime: {
                        "@type": "QuantitativeValue",
                        minValue: 0,
                        maxValue: 1,
                        unitCode: "DAY",
                      },
                      transitTime: {
                        "@type": "QuantitativeValue",
                        minValue: 1,
                        maxValue: 4,
                        unitCode: "DAY",
                      },
                    },
                  },
                  hasMerchantReturnPolicy: {
                    "@type": "MerchantReturnPolicy",
                    applicableCountry: "MA",
                    returnPolicyCategory:
                      "https://schema.org/MerchantReturnFiniteReturnWindow",
                    merchantReturnDays: 7,
                    returnMethod: "https://schema.org/ReturnByMail",
                    returnFees: "https://schema.org/FreeReturn",
                  },
                },
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Accueil", item: SITE },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: product.category,
                    item: `${SITE}${product.categoryTo ?? "/beachwear"}`,
                  },
                  { "@type": "ListItem", position: 3, name: product.name, item: url },
                ],
              }),
            },
          ]
        : undefined,

    };
  },
  component: ProductPage,
});


function ProductPage() {
  const { slug } = Route.useParams();
  const { product: shopifyProduct } = Route.useLoaderData();
  // Shopify prioritaire ; repli sur la fiche locale tant que le mapping n'est pas résolu.
  const product = shopifyProduct ?? productDetails[slug];

  if (!product) {
    const name = slug
      .split("-")
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    return (
      <CollectionPlaceholder
        eyebrow="Pièce"
        title={name}
        subtitle="La fiche produit complète (tailles, couleurs, photos) arrive prochainement."
      />
    );
  }

  return <ProductDetailView product={product} />;
}

function ProductDetailView({ product }: { product: ProductDetail | ShopProduct }) {
  const { addToCart, checkout, checkingOut, checkoutError } = useShop();
  const { data: reviews = [] } = useProductReviews(product.slug);
  const reviewStats = statsFromRatings(reviews.map((r) => r.rating));

  const shopProduct = "variants" in product ? (product as ShopProduct) : undefined;

  // Le panier a besoin des variantes Shopify pour créer le checkout.
  useEffect(() => {
    if (shopProduct) registerProducts([shopProduct]);
  }, [shopProduct]);

  const hasColors = (product.colors?.length ?? 0) > 0;
  const [colorId, setColorId] = useState(product.colors?.[0]?.id ?? "");
  const [size, setSize] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  /** CTA sticky mobile : visible dès que le bloc CTA principal sort de l'écran. */
  const ctaRef = useRef<HTMLDivElement | null>(null);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry!.isIntersecting && entry!.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const color = useMemo(
    () => product.colors?.find((c) => c.id === colorId) ?? product.colors?.[0],
    [product.colors, colorId],
  );
  const images = color?.images ?? product.images ?? [];
  const sizeOptions = color?.sizes ?? product.sizes ?? [];
  const hasSizes = sizeOptions.length > 0;

  const variantLabel = [color?.label ?? product.colorLabel, hasSizes && size ? `Taille ${size}` : product.sizeLabel]
    .filter(Boolean)
    .join(" · ");

  /** Disponibilité Shopify de la combinaison sélectionnée. */
  const selectedVariant = shopProduct?.variants.find(
    (v) =>
      (!color || !v.color || v.color.toLowerCase() === color.label.toLowerCase()) &&
      (!size || !v.size || v.size === size),
  );
  const soldOut = shopProduct ? selectedVariant?.available === false : false;

  const addLine = (openCart = true) => {
    if (hasSizes && !size) {
      setError("Veuillez sélectionner une taille.");
      return false;
    }
    setError(null);
    const variant = variantLabel;
    const merchandiseId = shopProduct
      ? findVariantId(shopProduct, { colorLabel: color?.label, size: size ?? undefined })
      : undefined;
    for (let i = 0; i < qty; i += 1) {
      addToCart(
        {
          id: [product.id, color?.id, size].filter(Boolean).join("-"),
          name: product.name,
          price: selectedVariant?.price ?? product.price,
          variant,
          productId: product.id,
          ...(color?.id ? { colorId: color.id } : {}),
          ...(size ? { size } : {}),
          ...(merchandiseId ? { merchandiseId } : {}),
        },
        openCart,
      );
    }
    return true;
  };


  return (
    <div className="bg-background pb-24 lg:pb-0">
      {/* pt- compense la hauteur du header fixe (barre d'annonce + navigation) */}
      <div className="mx-auto max-w-7xl px-5 pb-8 pt-28 sm:px-8 sm:pb-14 sm:pt-32 lg:px-10 lg:pt-48">

        <nav aria-label="Fil d'Ariane" className="label-xs mb-6 text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Accueil
          </Link>
          <span className="mx-2">/</span>
          <Link to={product.categoryTo ?? "/beachwear"} className="hover:text-foreground">
            {product.category}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{product.name}</span>
        </nav>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <ProductGallery images={images} alt={`${product.name}${color ? ` ${color.label}` : ""}`} />

          <div className="min-w-0 lg:pt-4">
            <p className="label-xs text-accent">
              {product.category} · {product.subcategory}
            </p>
            <h1 className="mt-3 font-serif text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              {product.name}
            </h1>
            <p className="mt-3 text-xl tabular-nums">{formatDH(product.price)}</p>

            <ReviewSummary
              count={reviewStats.count}
              average={reviewStats.average}
              onClick={() =>
                document.getElementById("avis")?.scrollIntoView({ behavior: "smooth" })
              }
            />




            {/* Couleur */}
            {hasColors && color ? (
            <div className="mt-8">
              <p className="label-xs" id="label-couleur">
                Couleur — <span className="text-muted-foreground">{color.label}</span>
              </p>
              <div className="mt-3 flex gap-3" role="radiogroup" aria-labelledby="label-couleur">
                {product.colors!.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    onClick={() => setColorId(c.id)}
                    onKeyDown={(e) => {
                      const list = product.colors!;
                      let next: number | null = null;
                      if (e.key === "ArrowRight" || e.key === "ArrowDown")
                        next = (i + 1) % list.length;
                      else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
                        next = (i - 1 + list.length) % list.length;
                      if (next === null) return;
                      e.preventDefault();
                      setColorId(list[next]!.id);
                      (e.currentTarget.parentElement?.children[next] as HTMLButtonElement | undefined)?.focus();
                    }}
                    aria-label={`Couleur ${c.label}`}
                    aria-checked={c.id === color.id}
                    tabIndex={c.id === color.id ? 0 : -1}
                    className={cn(
                      "h-8 w-8 rounded-full border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                      c.id === color?.id
                        ? "border-foreground ring-1 ring-foreground ring-offset-2 ring-offset-background"
                        : "border-border",
                    )}
                    style={{ backgroundColor: c.swatch }}
                  />
                ))}
              </div>
              <p aria-live="polite" className="sr-only">{`Couleur sélectionnée : ${color.label}`}</p>
            </div>
            ) : null}


            {/* Couleur / taille uniques */}
            {!hasColors && (product.colorLabel || product.sizeLabel) ? (
              <div className="mt-8 space-y-2 text-sm">
                {product.colorLabel ? (
                  <p className="label-xs">
                    Couleur — <span className="text-muted-foreground">{product.colorLabel}</span>
                  </p>
                ) : null}
                {product.sizeLabel ? (
                  <p className="label-xs">
                    Taille — <span className="text-muted-foreground">{product.sizeLabel}</span>
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* Taille */}
            {hasSizes ? (
            <div id="selection-taille" className="mt-8 scroll-mt-24">

              <div className="flex items-center justify-between">
                <p className="label-xs">Taille</p>
                <button
                  type="button"
                  onClick={() => setShowGuide((v) => !v)}
                  className="text-xs underline underline-offset-4 text-muted-foreground hover:text-foreground"
                >
                  Guide des tailles
                </button>
              </div>
              <div className="mt-3 flex gap-3">
                {sizeOptions.map((s) => (
                  <button
                    key={s.sku}
                    type="button"
                    onClick={() => {
                      setSize(s.size);
                      setError(null);
                    }}
                    aria-pressed={size === s.size}
                    className={cn(
                      "h-12 w-14 border text-sm transition-colors",
                      size === s.size
                        ? "border-foreground bg-foreground text-background"
                        : "border-border hover:border-foreground",
                    )}
                  >
                    {s.size}
                  </button>
                ))}
              </div>

              {showGuide ? (
                <div className="mt-4 border border-border bg-secondary/50 p-4 text-xs text-muted-foreground">
                  <p className="label-xs mb-2 text-foreground">Guide des tailles</p>
                  <ul className="space-y-1">
                    {sizeOptions.some((s) => s.size === "S") ? (
                      <li>S — FR 34/36 · Tour de poitrine 82-86 cm</li>
                    ) : null}
                    {sizeOptions.some((s) => s.size === "M") ? (
                      <li>M — FR 38/40 · Tour de poitrine 87-92 cm</li>
                    ) : null}
                    {sizeOptions.some((s) => s.size === "L") ? (
                      <li>L — FR 42/44 · Tour de poitrine 93-98 cm</li>
                    ) : null}
                    {sizeOptions.some((s) => s.size === "XL") ? (
                      <li>XL — FR 46/48 · Tour de poitrine 99-104 cm</li>
                    ) : null}
                  </ul>

                </div>
              ) : null}

              {error ? (
                <p role="alert" className="mt-3 text-sm text-destructive">
                  {error}
                </p>
              ) : null}
            </div>
            ) : null}

            <p className="mt-5 max-w-prose text-sm leading-relaxed text-muted-foreground sm:text-base">
              {product.shortDescription}
            </p>

            {/* Quantité */}
            <div className="mt-8">
              <p className="label-xs">Quantité</p>
              <div className="mt-3 inline-flex items-center border border-border">
                <button
                  type="button"
                  aria-label="Diminuer la quantité"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="grid h-12 w-12 place-items-center hover:bg-secondary"
                >
                  <Minus size={15} strokeWidth={1.4} />
                </button>
                <span className="w-12 text-center text-sm tabular-nums">{qty}</span>
                <button
                  type="button"
                  aria-label="Augmenter la quantité"
                  onClick={() => setQty((q) => Math.min(10, q + 1))}
                  className="grid h-12 w-12 place-items-center hover:bg-secondary"
                >
                  <Plus size={15} strokeWidth={1.4} />
                </button>
              </div>
            </div>

            {/* CTA */}
            <div ref={ctaRef} className="mt-8 space-y-3">
              <button
                type="button"
                onClick={() => addLine()}
                disabled={soldOut}
                className="btn-primary label-xs h-14 w-full shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                {soldOut ? "Rupture de stock" : `Ajouter au panier — ${formatDH(selectedVariant?.price ?? product.price)}`}
              </button>
              <button
                type="button"
                disabled={soldOut || checkingOut}
                onClick={() => {
                  if (!addLine(false)) return;
                  const merchandiseId = shopProduct
                    ? findVariantId(shopProduct, { colorLabel: color?.label, size: size ?? undefined })
                    : undefined;
                  void checkout(
                    merchandiseId
                      ? [
                          {
                            merchandiseId,
                            quantity: qty,
                            name: product.name,
                            price: selectedVariant?.price ?? product.price,
                            variant: variantLabel,
                          },
                        ]
                      : undefined,
                  );
                }}
                className="btn-secondary label-xs h-14 w-full disabled:cursor-not-allowed disabled:opacity-40"
              >
                {checkingOut ? "Redirection…" : hasSizes ? "Commander maintenant" : "Commander"}
              </button>

              {checkoutError ? (
                <p role="alert" className="text-sm text-destructive">
                  {checkoutError}
                </p>
              ) : null}
            </div>



            <div className="mt-5 space-y-1 text-xs text-muted-foreground">
              <p>Paiement à la livraison</p>
              <p>Livraison partout au Maroc</p>
            </div>

            {/* Détails */}
            <div className="mt-10 border-t border-border pt-6">
              <p className="label-xs">Détails</p>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {product.details.map((d) => (
                  <li key={d} className="flex gap-2">
                    <span aria-hidden="true" className="text-accent">
                      —
                    </span>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ProductReviews productSlug={product.slug} productName={product.name} />

      {/* Barre d'achat sticky mobile (safe-area iPhone) */}
      <div
        role="region"
        aria-label="Achat rapide"
        aria-hidden={!showSticky}
        {...(!showSticky ? { inert: "" as unknown as boolean } : {})}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur-md transition-all duration-300 lg:hidden",
          showSticky
            ? "visible translate-y-0 opacity-100"
            : "invisible pointer-events-none translate-y-full opacity-0",
        )}
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
      >
        <div className="flex items-center gap-3 px-4 pt-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-serif text-sm leading-tight">{product.name}</p>
            <p className="text-sm tabular-nums">
              {formatDH(product.price)}
              {hasSizes ? (
                <span className="label-xs ml-2 text-muted-foreground">
                  {size ? `Taille ${size}` : "Choisir une taille"}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            tabIndex={showSticky ? 0 : -1}
            aria-label={
              hasSizes && !size
                ? `Choisir une taille pour ${product.name}`
                : `Ajouter ${product.name}${size ? ` taille ${size}` : ""} au panier — ${formatDH(product.price)}`
            }
            onClick={() => {
              if (!addLine()) {
                const target = document.getElementById("selection-taille");
                target?.scrollIntoView({ behavior: "smooth", block: "center" });
                target?.querySelector<HTMLButtonElement>("button[aria-pressed]")?.focus();
              }
            }}
            className="btn-primary label-xs h-12 shrink-0 px-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Ajouter
          </button>
        </div>
        <p aria-live="polite" className="sr-only">
          {error ?? ""}
        </p>
      </div>

    </div>
  );
}

